import React, { useRef, useState } from 'react';
import useForm from '@/hooks/useForm';
import { States } from '@/helpers/common/states';
import { AxiosError } from 'axios';
import api from '@/services/api';
import { REACT_APP_REPORT_BASE_URL } from '@/utils/config';
import { ChargeBack } from '@/model/ChargeBack';
import { toast } from 'react-toastify';
import { useConfirmDelete } from '@/hooks/useConfirmDelete';
import validators from '@/helpers/validators';
import { exportChargebackPdf } from '../exportPdf';
import { ChargebackContainer } from './ui';
import { FormInputName } from '../types';
import { CancelContent } from '../components/CancelContent';

export const ChargebackScreen: React.FC = (): JSX.Element => {
  const [state, setState] = useState<States>(States.default);
  const [chargeBack, setChargeBack] = useState<ChargeBack>();
  const confirmDelete = useConfirmDelete();
  const exporting = useRef(false);
  const { formData, formErrors, onChangeFormInput, isFormValid, resetForm } = useForm({
    initialData: {
      transactionCode: '',
    },
    validators: {
      transactionCode: [validators.required],
    },
  });

  const handleOnExport = async (): Promise<void> => {
    if (exporting.current || state === States.loading) return;
    exporting.current = true;
    setState(States.loading);
    try {
      const report = document.getElementById('ExportToPdf');
      if (!report) throw new Error('Relatório indisponível para exportação.');
      await exportChargebackPdf(report);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Não foi possível exportar o relatório.',
      );
    } finally {
      exporting.current = false;
      setState(States.default);
    }
  };
  const handleOnFilter = async (): Promise<void> => {
    if (isFormValid()) {
      try {
        setState(States.loading);
        setChargeBack(undefined);
        const { data } = await api.get<ChargeBack>(
          `/event/report/${formData[FormInputName.transactionCode]}/chargeback`,
          { baseURL: REACT_APP_REPORT_BASE_URL || api.defaults.baseURL },
        );
        if (data?.order && data.pagseguro) {
          setChargeBack(data);
          resetForm();
        }
      } catch (error) {
        const err = error as AxiosError<{ message: string }>;
        toast.error(err.response?.data?.message || err.message);
      } finally {
        setState(States.default);
      }
    }
  };

  const handleOnCancelOrder = async (): Promise<void> => {
    if (!chargeBack?.order?.id) {
      return;
    }

    try {
      setState(States.loading);
      await api.patch(`/support/chargeback/cancel/${chargeBack.order.id}`);
      toast.success('Pedido cancelado com sucesso e clientes marcados com alerta de fraude!');
      confirmDelete.hide();
    } catch (error) {
      const err = error as AxiosError;
      toast.error(err.message);
    } finally {
      setState(States.default);
    }
  };

  const handleOnShowCancelOrder = (): void => {
    confirmDelete.show({
      title: '',
      children: <CancelContent />,
      actions: [
        {
          title: 'Não, quero manter',
          theme: 'noneBorder',
          onClick: (): void => confirmDelete.hide(),
        },
        {
          title: 'Sim, quero cancelar',
          onClick: (): Promise<void> => handleOnCancelOrder(),
        },
      ],
    });
  };

  return (
    <ChargebackContainer
      state={state}
      formData={formData}
      formErrors={formErrors}
      onChangeFormInput={onChangeFormInput}
      onFilter={handleOnFilter}
      chargeBack={chargeBack}
      onExport={handleOnExport}
      onCancelOrder={handleOnShowCancelOrder}
    />
  );
};
